export type Application = "QM" | "AUTH" | "COHERE_LAUNCHER";
export type Category = "WORKING" | "NON_WORKING";
type AggregationStatus = "SKIPPED" | "NOT_STARTED" | "COMPLETE";
export interface TimeTracker {
  id: string;
  category: Category;
  caseNumber?: string;
  caseId?: string;
  application: Application;
  component: string;
  active: boolean;
  duration: number;
  stoppedReason: string;
  dateCreated: string;
  dateCreatedAsLong?: number;
  lastUpdated: string;
  lastUpdatedAsLong?: number;
  createdBy: string;
  updatedBy: string;
  subComponent?: string;
  healthPlan?: string;
  delegatedVendor?: string;
  aggregationStatus?: AggregationStatus;
  timeStoppedAsLong?: number;
  queueId?: string;
  // Cohere Launcher fields (RW-743/RW-744)
  source?: Application;
  serviceRequestId?: string;
  uniqueIdentifier?: string;
  reviewId?: string;
  noteId?: string;
  letterId?: string;
}
export interface TimeTrackerRequestBody {
  category: Category;
  caseId?: string;
  application: Application;
  component: string;
  // Cohere Launcher fields (RW-747). Launcher trackers omit caseId/caseNumber/queueId and send
  // source + serviceRequestId (+ reviewId/tenancy); uniqueIdentifier is server-generated.
  source?: Application;
  serviceRequestId?: string;
  reviewId?: string;
  healthPlan?: string;
  delegatedVendor?: string;
}
interface TrackConfig {
  application: Application;
  component: string;
  category: Category;
  active: boolean;
  /* Optionally define a list of query param names that must exist in path in order for tracker to act */
  requiredParams?: string[];
  /* RW-746/RW-748: route is tracked ONLY when the Cohere Launcher origin is active (note/letter
   * actions have no equivalent in the normal AUTH flow, so they must not start AUTH trackers). */
  launcherOnly?: boolean;
  /* RW-748: under launcher origin, hold the tracker POST until a reviewId is present in the URL.
   * These review routes create the Review (with an id) at start action and sync it to the URL, so
   * the launcher tracker should be created once, already carrying reviewId (avoids a double-POST).
   * Note/letter/outreach routes have no reviewId and leave this false (they POST immediately). */
  launcherRequiresReviewId?: boolean;
}
export interface ServiceCase {
  id: string;
}
const config: Record<string, TrackConfig> = {
  "/dashboard": {
    application: "QM",
    component: "Dashboard",
    category: "NON_WORKING",
    active: true,
    requiredParams: ["queueId"],
  },
  "/case_complete/:caseId": {
    application: "QM",
    component: "Case Complete",
    category: "NON_WORKING",
    active: true,
    requiredParams: ["queueId"],
  },
  "/:serviceRequestId/review": {
    application: "AUTH",
    component: "Clinical Review",
    category: "WORKING",
    active: true,
    // Launcher: Clinical/MD/OON/P2P/Audit reviews create a Review (with id) at start action; wait
    // for that reviewId (synced to the URL) before POSTing so the tracker carries it from creation.
    launcherRequiresReviewId: true,
  },
  "/denial_review/:serviceRequestId": {
    application: "AUTH",
    component: "Denial Review",
    category: "WORKING",
    active: true,
    requiredParams: ["reviewId"],
    launcherRequiresReviewId: true,
  },
  "/:serviceRequestId/outreach": {
    application: "AUTH",
    component: "Outreach",
    category: "WORKING",
    active: true,
    requiredParams: ["caseId"],
  },
  "/fax/:faxId": {
    application: "AUTH",
    component: "Fax Attachment",
    category: "WORKING",
    active: true,
    requiredParams: ["caseId"],
  },
  "/fax_service_request/:faxId": {
    application: "AUTH",
    component: "Fax New SR",
    category: "WORKING",
    active: true,
    requiredParams: ["caseId"],
  },
  "/auth_builder/:patientId": {
    application: "AUTH",
    component: "Fax New SR Auth Builder",
    category: "WORKING",
    active: true,
    requiredParams: ["faxId", "caseId"],
  },
  // Cohere Launcher note/letter actions (RW-748). launcherOnly: these routes have no equivalent
  // in the normal AUTH flow, so they only track when the launcher origin is active. Components use
  // the launcher action labels (CohereLauncherActions.launcherActionLabel). No requiredParams:
  // launcher trackers are case-agnostic and start before noteId/letterId exist (attached later).
  "/:serviceRequestId/new-note": {
    application: "AUTH",
    component: "Note",
    category: "WORKING",
    active: true,
    launcherOnly: true,
  },
  "/:serviceRequestId/appeal-note": {
    application: "AUTH",
    component: "Appeal note",
    category: "WORKING",
    active: true,
    launcherOnly: true,
  },
  "/:serviceRequestId/redetermination-note": {
    application: "AUTH",
    component: "Redetermination note",
    category: "WORKING",
    active: true,
    launcherOnly: true,
  },
  "/letter/:serviceRequestId": {
    application: "AUTH",
    component: "Letter",
    category: "WORKING",
    active: true,
    launcherOnly: true,
  },
  "/adhoc_letter/:serviceRequestId": {
    application: "AUTH",
    component: "Letter",
    category: "WORKING",
    active: true,
    launcherOnly: true,
  },
};
export default config;
